import { storage } from '@uhg-abyss/web/tools/storage';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import React, { useEffect } from 'react';
import { useSessionStorage } from 'usehooks-ts';

import { Constants } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../../../common/Utils';
import { useLagoon } from '../../../../hooks/useLagoon';
import { usePlanSelector } from '../../../../hooks/usePlanSelector';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { usePlanSelectorStore } from '../../../../store/usePlanSelectorStore';
import { useVirtualCareStore } from '../../../../store/useVirtualCareStore';
import { loginNotRequired } from '../../../../utils/session.utils';
import { IChildrenProp } from '../../types';
import SharedAccessErrorModal from '../SharedAccessErrorModal/SharedAccessErrorModal';
import { getPlanSelectorVariables } from './helpers';

export const PlanSelector = ({ children }: IChildrenProp) => {
  const [planSelector, setPlanSelector] = usePlanSelector({});
  const [otherPlansSelector, setOtherPlansSelector] = usePlanSelector({});
  const [, setMemberId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MEMBER_ID,
    0
  );
  const featureFlags = useLagoon('feature-flags')();
  const planSelectorFeatureFlag: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLAN_SELECTION_V2
  );
  const enablePlanTypeSelectionInFilter: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLAN_TYPE_SELECTION_IN_FILTER
  );
  const isSharedAccess: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SHARED_ACCESS
  );
  const planContextIds = storage.cookie.get(Constants.COOKIES.PLAN_CONTEXT_IDS);

  const { setBigN, clearPlanInfo } = usePlanSelectorStore((state) => state);
  const { clearEligibilities } = useVirtualCareStore((state) => state);

  const { planToken, accountId } = getPlanSelectorVariables();

  const setPlanSelectorState = useStore(StoreKeys.SET_PLAN_SELECTOR);
  const setOtherPlansSelectorState = useStore(
    StoreKeys.SET_OTHER_PLANS_SELECTOR
  );
  const { isLoading, data } = planSelector;
  const isLoggedInMember = data?.planSelector?.isLoggedInMember;
  const isLocal = loginNotRequired();

  useEffect(() => {
    if (!isLocal) {
      const fetchPlan = async () => {
        const result = await setPlanSelector({
          variables: {
            planToken,
            accountId,
            enablePlanSelectorV2: planSelectorFeatureFlag,
          },
        });
        if (result?.data?.planSelector?.bigN) {
          setBigN(planToken, result.data.planSelector.bigN);
        }
      };
      fetchPlan();
    }
  }, []);

  useEffect(() => {
    // Compute otherPlanTokens inside the effect to always use latest values
    clearPlanInfo(planContextIds);
    clearEligibilities(planContextIds);
    const otherPlanTokens =
      planContextIds && planToken
        ? planContextIds
            .split(':')
            .filter((token) => Boolean(token) && token !== planToken)
        : [];
    if (
      !isLocal &&
      planContextIds &&
      enablePlanTypeSelectionInFilter &&
      otherPlanTokens.length > 0
    ) {
      const fetchPlans = async () => {
        for (const token of otherPlanTokens) {
          const result = await setOtherPlansSelector({
            variables: {
              planToken: token,
              accountId,
              enablePlanSelectorV2: planSelectorFeatureFlag,
            },
          });
          if (result?.data?.planSelector?.bigN) {
            setBigN(token, result.data.planSelector.bigN);
          }
        }
      };
      fetchPlans();
    }
  }, [planContextIds]);

  useEffect(() => {
    if (planSelector?.data) {
      setPlanSelectorState(planSelector?.data?.planSelector);
      const { MemberNumber } = planSelector?.data?.planSelector?.bigN;
      /** MNR and ENI will have MemberNumber from PlanSelector , no need for userName as alternate check.
       */
      setMemberId(MemberNumber);
    }
  }, [planSelector]);

  useEffect(() => {
    if (otherPlansSelector?.data) {
      setOtherPlansSelectorState(otherPlansSelector?.data?.planSelector);
    }
  }, [otherPlansSelector]);

  //to check ENABLE_SHARED_ACCESS is not undefined,  we only want to check if it is false from lagoon
  const careGiver = isSharedAccess === false;

  return (
    <React.Fragment>
      {isLoading ? (
        <LoadingSpinner
          ariaLoadingLabel="Loading page"
          isLoading={isLoading}
          size="xs"
        />
      ) : (
        <React.Fragment>
          {isLoggedInMember === false && careGiver ? (
            <SharedAccessErrorModal />
          ) : (
            children
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
